import { FC, Fragment, MouseEvent, useCallback } from "react";
import {
  Button,
  Menu,
  MenuItem,
  PopoverOrigin,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AvTimerIcon from "@mui/icons-material/AvTimer";
import {
  AutoFixHighOutlined,
  NotificationsActiveOutlined,
} from "@mui/icons-material";
import { useAppAbility } from "@/services/permissions";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { ScheduleActionType } from "@/services/cloudchipr.api";
import { getUserPermissionInfoText } from "@/components/pages/utils/helpers/getUserPermissionInfoText";

export type LiveResourcesCreateWorkflowCustomTrigger = FC<{
  onClick(event: MouseEvent<HTMLElement>): void;
  disabled?: boolean;
}>;

interface LiveResourcesCreateWorkflowProps {
  disableActionItems?: boolean;
  actionItemsTooltipTitle?: string;
  onItemClick(action: ScheduleActionType): void;
  CustomTrigger?: LiveResourcesCreateWorkflowCustomTrigger;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

export const LiveResourcesCreateWorkflow: FC<
  LiveResourcesCreateWorkflowProps
> = ({
  disableActionItems,
  onItemClick,
  CustomTrigger,
  transformOrigin,
  anchorOrigin,
  actionItemsTooltipTitle,
}) => {
  const { anchor, open, openMenu, closeMenu } = useMenuHook();
  const { cannot } = useAppAbility();
  const canNotCreateSchedule = cannot("create", "schedule");

  const actionClickHandler = useCallback(() => {
    onItemClick("clean_and_notify");
    closeMenu();
  }, [onItemClick, closeMenu]);

  const notifyClickHandler = useCallback(() => {
    onItemClick("notify");
    closeMenu();
  }, [onItemClick, closeMenu]);

  const tooltipTitle = canNotCreateSchedule
    ? getUserPermissionInfoText("Create Workflow")
    : "Create Workflow";

  return (
    <Fragment>
      {CustomTrigger ? (
        <CustomTrigger onClick={openMenu} disabled={canNotCreateSchedule} />
      ) : (
        <Tooltip title={tooltipTitle} arrow placement="top">
          <span>
            <Button
              color="primary"
              variant="outlined"
              sx={buttonStyles}
              onClick={openMenu}
              disabled={canNotCreateSchedule}
            >
              <AvTimerIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>
      )}

      <Menu
        open={open}
        onClose={closeMenu}
        anchorEl={anchor}
        anchorOrigin={anchorOrigin ?? defaultAnchorOrigin}
        transformOrigin={transformOrigin ?? defaultTransformOrigin}
      >
        <Tooltip arrow title={actionItemsTooltipTitle} placement="top">
          <span>
            <MenuItem
              disabled={disableActionItems}
              onClick={actionClickHandler}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AutoFixHighOutlined fontSize="small" />
                <Typography variant="body2">Take Action</Typography>
              </Stack>
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem onClick={notifyClickHandler}>
          <Stack direction="row" spacing={1} alignItems="center">
            <NotificationsActiveOutlined fontSize="small" />
            <Typography variant="body2">Notify Only</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </Fragment>
  );
};

const defaultAnchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "left",
};
const defaultTransformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: "left",
};

const buttonStyles = {
  minWidth: 36,
  minHeight: 36,
  px: 0.5,
};
